import React, { useEffect } from "react";
import ComponentWrapper from "../components/Shared/ComponentWrapper";

const PrivacyPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="w-full min-h-screen bg-white">
      <ComponentWrapper styles="pt-12 sm:pt-20 pb-20">
        <div className="max-w-[800px] mx-auto">
          <h1 className="text-[36px] sm:text-[48px] font-semibold text-blue-1 leading-tight mb-2">
            Privacy Policy
          </h1>
          <p className="text-[14px] text-gray-1 mb-10">
            Last updated: March 16, 2026
          </p>

          <div className="prose-custom">
            <Section title="1. Introduction">
              <P>
                This Privacy Policy describes how Helix AI ("Helix", "we", "us",
                or "our") collects, uses, stores, shares, and protects
                information when you use the Helix AI platform, including the
                website at helix.org, the application at ai.helix.org, and all
                associated services (collectively, the "Services").
              </P>
              <P>
                We are committed to protecting your privacy and handling your
                data with transparency. By using the Services, you consent to the
                data practices described in this Privacy Policy. If you do not
                agree with this Privacy Policy, you should not use the Services.
              </P>
            </Section>

            <Section title="2. Information We Collect">
              <H3>2.1 Information You Provide</H3>
              <P>
                When you use the Services, we may collect the following
                information that you voluntarily provide:
              </P>
              <Ul>
                <Li>
                  <strong>Wallet address:</strong> Your public blockchain wallet
                  address when you connect to the Services. This is a public
                  blockchain identifier and not personally identifiable
                  information on its own.
                </Li>
                <Li>
                  <strong>Authentication data:</strong> If you sign in using
                  Google, Apple, email, or phone, we receive limited profile
                  information from the authentication provider (such as email
                  address or phone number) as necessary to create and manage your
                  session.
                </Li>
                <Li>
                  <strong>Chat messages:</strong> The messages you send to the
                  Helix AI agent, including instructions, queries, and any
                  information you voluntarily share in conversation.
                </Li>
                <Li>
                  <strong>Transaction data:</strong> Records of transactions
                  initiated through the Services, including smart contract
                  deployments, token swaps, and transfers.
                </Li>
              </Ul>

              <H3>2.2 Information Collected Automatically</H3>
              <P>
                When you access the Services, we may automatically collect
                certain information, including:
              </P>
              <Ul>
                <Li>
                  <strong>Device information:</strong> Device type, operating
                  system, browser type and version, screen resolution, and
                  language preferences.
                </Li>
                <Li>
                  <strong>Usage data:</strong> Pages visited, features used,
                  click patterns, session duration, and referring URLs.
                </Li>
                <Li>
                  <strong>Log data:</strong> IP address, access times, server
                  logs, and error reports.
                </Li>
                <Li>
                  <strong>Cookies and similar technologies:</strong> We use
                  cookies, local storage, and similar technologies to maintain
                  your session, remember your preferences, and improve the
                  Services. See Section 8 for more details.
                </Li>
              </Ul>

              <H3>2.3 Information from Third Parties</H3>
              <P>We may receive information from third-party services including:</P>
              <Ul>
                <Li>
                  <strong>Blockchain networks:</strong> Publicly available
                  on-chain data associated with your wallet address, including
                  token balances, transaction history, and smart contract
                  interactions.
                </Li>
                <Li>
                  <strong>Authentication providers:</strong> Limited profile
                  information from Google, Apple, or other sign-in providers as
                  described in Section 2.1.
                </Li>
                <Li>
                  <strong>Analytics providers:</strong> Aggregated usage
                  statistics and performance metrics.
                </Li>
              </Ul>
            </Section>

            <Section title="3. How We Use Your Information">
              <P>We use the information we collect for the following purposes:</P>
              <Ul>
                <Li>
                  <strong>Providing the Services:</strong> Processing your
                  requests, executing blockchain transactions, deploying smart
                  contracts, and delivering AI-powered responses.
                </Li>
                <Li>
                  <strong>Account management:</strong> Managing your credit
                  balance, processing HLX token purchases, and maintaining your
                  session.
                </Li>
                <Li>
                  <strong>Service improvement:</strong> Analyzing usage patterns
                  to improve the AI agent's performance, accuracy, and
                  capabilities.
                </Li>
                <Li>
                  <strong>Security:</strong> Detecting and preventing fraud,
                  abuse, security incidents, and violations of our Terms of
                  Service.
                </Li>
                <Li>
                  <strong>Communication:</strong> Sending service-related
                  notifications, updates, and important announcements.
                </Li>
                <Li>
                  <strong>Legal compliance:</strong> Complying with applicable
                  laws, regulations, and legal processes.
                </Li>
                <Li>
                  <strong>Analytics:</strong> Understanding how users interact
                  with the Services to inform product development and business
                  decisions.
                </Li>
              </Ul>
            </Section>

            <Section title="4. AI and Chat Data">
              <P>
                When you interact with the Helix AI agent, your messages are
                processed by AI language models to generate responses and execute
                actions. You should be aware of the following:
              </P>
              <Ul>
                <Li>
                  Chat messages may be processed by third-party AI model
                  providers (such as OpenAI) to generate responses. These
                  providers have their own privacy policies governing data
                  handling.
                </Li>
                <Li>
                  We may retain chat history to provide conversation continuity
                  and improve the quality of the Services.
                </Li>
                <Li>
                  We do not use your chat data to train AI models without your
                  explicit consent.
                </Li>
                <Li>
                  You should not share sensitive personal information (such as
                  passwords, private keys, seed phrases, or government-issued
                  identification numbers) in chat messages.
                </Li>
                <Li>
                  Aggregated and anonymized chat data may be used for service
                  improvement and analytics purposes.
                </Li>
              </Ul>
            </Section>

            <Section title="5. Blockchain and On-Chain Data">
              <P>
                It is important to understand the nature of blockchain technology
                with respect to privacy:
              </P>
              <Ul>
                <Li>
                  Blockchain transactions are inherently public and immutable.
                  Any transaction you initiate through the Services will be
                  permanently recorded on the respective blockchain and visible
                  to anyone.
                </Li>
                <Li>
                  Your wallet address and all associated transaction history are
                  publicly viewable on blockchain explorers.
                </Li>
                <Li>
                  Helix AI cannot delete, modify, or reverse any on-chain
                  transaction or data.
                </Li>
                <Li>
                  Smart contracts deployed through the Services will contain
                  publicly visible code on the blockchain.
                </Li>
                <Li>
                  We do not have access to your private keys and cannot control
                  your on-chain assets.
                </Li>
              </Ul>
            </Section>

            <Section title="6. Data Sharing and Disclosure">
              <P>
                We do not sell your personal information. We may share your
                information in the following circumstances:
              </P>
              <Ul>
                <Li>
                  <strong>Service providers:</strong> With trusted third-party
                  service providers who assist us in operating the Services,
                  including hosting providers, AI model providers, analytics
                  services, and wallet infrastructure providers. These providers
                  are contractually obligated to protect your data.
                </Li>
                <Li>
                  <strong>Blockchain networks:</strong> Transaction data is
                  broadcast to public blockchain networks as a necessary part of
                  executing your requested operations.
                </Li>
                <Li>
                  <strong>Third-party protocols:</strong> When you use features
                  that integrate with third-party services (such as DEX
                  aggregators), your transaction data is
                  shared with those services as necessary to execute your
                  request.
                </Li>
                <Li>
                  <strong>Legal requirements:</strong> When required by law,
                  subpoena, court order, or governmental regulation, or when we
                  believe disclosure is necessary to protect our rights, prevent
                  fraud, or ensure user safety.
                </Li>
                <Li>
                  <strong>Business transfers:</strong> In connection with a
                  merger, acquisition, reorganization, or sale of assets, your
                  information may be transferred as part of that transaction.
                </Li>
                <Li>
                  <strong>With your consent:</strong> When you have given us
                  explicit consent to share your information for a specific
                  purpose.
                </Li>
              </Ul>
            </Section>

            <Section title="7. Data Security">
              <P>
                We implement reasonable technical and organizational measures to
                protect your information from unauthorized access, alteration,
                disclosure, or destruction. These measures include:
              </P>
              <Ul>
                <Li>Encryption of data in transit using TLS/SSL protocols.</Li>
                <Li>Encryption of sensitive data at rest.</Li>
                <Li>
                  Regular security assessments and vulnerability testing.
                </Li>
                <Li>Access controls and authentication for internal systems.</Li>
                <Li>
                  Secure session management with appropriate expiration
                  policies.
                </Li>
              </Ul>
              <P>
                However, no method of electronic storage or transmission over the
                internet is 100% secure. While we strive to use commercially
                acceptable means to protect your information, we cannot guarantee
                its absolute security.
              </P>
            </Section>

            <Section title="8. Cookies and Tracking Technologies">
              <P>
                We use the following types of cookies and similar technologies:
              </P>
              <Ul>
                <Li>
                  <strong>Essential cookies:</strong> Required for the Services
                  to function properly, including session management and
                  authentication.
                </Li>
                <Li>
                  <strong>Preference cookies:</strong> Used to remember your
                  settings and preferences across sessions.
                </Li>
                <Li>
                  <strong>Analytics cookies:</strong> Used to understand how users
                  interact with the Services, including page views, feature
                  usage, and performance metrics.
                </Li>
              </Ul>
              <P>
                You can control cookie settings through your browser preferences.
                Disabling essential cookies may impair the functionality of the
                Services.
              </P>
            </Section>

            <Section title="9. Data Retention">
              <P>
                We retain your information for as long as necessary to provide
                the Services and fulfill the purposes described in this Privacy
                Policy, unless a longer retention period is required or permitted
                by law. Specifically:
              </P>
              <Ul>
                <Li>
                  <strong>Account data:</strong> Retained for the duration of
                  your account and for a reasonable period thereafter.
                </Li>
                <Li>
                  <strong>Chat history:</strong> Retained to provide conversation
                  continuity. You may request deletion of your chat history.
                </Li>
                <Li>
                  <strong>Transaction records:</strong> Retained for compliance
                  and audit purposes as required by applicable law.
                </Li>
                <Li>
                  <strong>Log data:</strong> Generally retained for up to 90
                  days for security and debugging purposes.
                </Li>
                <Li>
                  <strong>Credit and purchase records:</strong> Retained for
                  accounting and compliance purposes.
                </Li>
              </Ul>
            </Section>

            <Section title="10. Your Rights and Choices">
              <P>
                Depending on your jurisdiction, you may have certain rights
                regarding your personal information, including:
              </P>
              <Ul>
                <Li>
                  <strong>Access:</strong> The right to request access to the
                  personal information we hold about you.
                </Li>
                <Li>
                  <strong>Correction:</strong> The right to request correction of
                  inaccurate or incomplete personal information.
                </Li>
                <Li>
                  <strong>Deletion:</strong> The right to request deletion of
                  your personal information, subject to certain exceptions.
                </Li>
                <Li>
                  <strong>Portability:</strong> The right to receive your
                  personal information in a structured, machine-readable format.
                </Li>
                <Li>
                  <strong>Objection:</strong> The right to object to certain
                  processing of your personal information.
                </Li>
                <Li>
                  <strong>Withdrawal of consent:</strong> The right to withdraw
                  consent where processing is based on consent.
                </Li>
              </Ul>
              <P>
                Please note that on-chain data cannot be deleted or modified due
                to the immutable nature of blockchain technology. To exercise
                your rights regarding off-chain data, please contact us through
                the channels listed in Section 14.
              </P>
            </Section>

            <Section title="11. International Data Transfers">
              <P>
                The Services are operated globally, and your information may be
                transferred to, stored, and processed in countries other than
                your country of residence. These countries may have different
                data protection laws than your jurisdiction. By using the
                Services, you consent to the transfer of your information to
                these countries.
              </P>
              <P>
                Where required by applicable law, we implement appropriate
                safeguards for cross-border data transfers, including standard
                contractual clauses or other legally approved transfer
                mechanisms.
              </P>
            </Section>

            <Section title="12. Children's Privacy">
              <P>
                The Services are not intended for individuals under the age of
                18. We do not knowingly collect personal information from
                children. If we become aware that we have collected personal
                information from a child under 18, we will take steps to delete
                that information promptly. If you believe that a child under 18
                has provided us with personal information, please contact us
                immediately.
              </P>
            </Section>

            <Section title="13. Changes to This Privacy Policy">
              <P>
                We may update this Privacy Policy from time to time to reflect
                changes in our practices, technology, legal requirements, or
                other factors. When we make material changes, we will notify you
                by updating the "Last updated" date at the top of this page and,
                where appropriate, providing additional notice through the
                Services.
              </P>
              <P>
                Your continued use of the Services after any changes to this
                Privacy Policy constitutes your acceptance of the updated policy.
                We encourage you to review this Privacy Policy periodically.
              </P>
            </Section>

            <Section title="14. Contact Us">
              <P>
                If you have questions, concerns, or requests regarding this
                Privacy Policy or our data practices, please contact us through
                our community channels:
              </P>
              <Ul>
                <Li>
                  Telegram:{" "}
                  <a
                    href="https://t.me/Helix"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-2 hover:underline font-medium"
                  >
                    t.me/Helix
                  </a>
                </Li>
                <Li>
                  X (Twitter):{" "}
                  <a
                    href="https://x.com/Helix"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-2 hover:underline font-medium"
                  >
                    x.com/Helix
                  </a>
                </Li>
              </Ul>
            </Section>
          </div>
        </div>
      </ComponentWrapper>
    </div>
  );
};

export default PrivacyPage;

function Section({ title, children }) {
  return (
    <section className="mb-10">
      <h2 className="text-[22px] sm:text-[24px] font-semibold text-blue-1 mb-4">
        {title}
      </h2>
      {children}
    </section>
  );
}

function H3({ children }) {
  return (
    <h3 className="text-[17px] font-semibold text-blue-1 mt-5 mb-2">
      {children}
    </h3>
  );
}

function P({ children }) {
  return (
    <p className="text-[15px] text-gray-600 leading-[26px] mb-3">{children}</p>
  );
}

function Ul({ children }) {
  return (
    <ul className="list-disc list-outside space-y-2 mb-4 pl-5">{children}</ul>
  );
}

function Li({ children }) {
  return (
    <li className="text-[15px] text-gray-600 leading-[24px]">{children}</li>
  );
}
